import { Button } from "@/components/flexkit/Button";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { TrackLocations } from "@/hooks/use-segment";
import {
    useDeleteHostAuthenticationToken,
    type PlainHostAuthenticationToken,
} from "@/queries/runner-configuration-queries";
import { type PlainRunner } from "@/queries/runner-queries";
import { formatError } from "@/utils/errors";
import { useCallback, type FC } from "react";
import type { SCMIntegration } from "gitpod-next-api/gitpod/v1/runner_configuration_pb";
import type { ServiceAccountHeaders } from "@/api";
import { ServiceAccountGitAuthenticationDetails } from "@/components/settings/organization/service-accounts/ServiceAccountGitAuthenticationDetails";

export type DeleteGitAuthenticationDialogProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    token: PlainHostAuthenticationToken;
    runner?: PlainRunner;
    scmIntegration?: SCMIntegration;
    onSuccess?: () => void;
    headers: ServiceAccountHeaders;
};

export const DeleteGitAuthenticationDialog: FC<DeleteGitAuthenticationDialogProps> = ({
    open,
    onOpenChange,
    token,
    runner,
    scmIntegration,
    onSuccess,
    headers,
}) => {
    const { toast } = useToast();
    const deleteToken = useDeleteHostAuthenticationToken({ headers });

    const handleConfirm = useCallback(async () => {
        try {
            await deleteToken.mutateAsync({ tokenId: token.id });
            onOpenChange(false);
            onSuccess?.();
        } catch (error) {
            toast({
                title: "Failed to remove Git authentication",
                description: formatError(error),
            });
        }
    }, [deleteToken, token.id, toast, onOpenChange, onSuccess]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid="delete-git-authentication-dialog"
                data-track-location={TrackLocations.ServiceAccountGitAuthenticationDeleteDialog}
                className="max-w-md"
            >
                <DialogHeader>
                    <DialogTitle>Remove Git Authentication</DialogTitle>
                    <DialogDescription>Are you sure you want to remove this Git authentication?</DialogDescription>
                </DialogHeader>

                <DialogBody>
                    <div className="flex gap-1 rounded-xl border border-border-subtle px-3 py-4">
                        <ServiceAccountGitAuthenticationDetails
                            token={token}
                            runner={runner}
                            scmIntegration={scmIntegration}
                        />
                    </div>
                </DialogBody>

                <DialogFooter>
                    <DialogClose asChild>
                        <Button
                            variant="outline"
                            disabled={deleteToken.isPending}
                            data-testid="delete-git-authentication-dialog-cancel"
                        >
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        variant="destructive"
                        onClick={handleConfirm}
                        loading={deleteToken.isPending}
                        disabled={deleteToken.isPending}
                        data-testid="delete-git-authentication-dialog-confirm"
                        data-tracking-id="delete-git-authentication"
                    >
                        Yes, Delete
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
